import {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import {
  toolbarButtonStyle,
  toolbarFieldActiveStyle,
} from '../styles';
import {css} from '@emotion/react';
import {FieldLabel} from '../FieldLabel';
import {Toggle} from '~/components/Toggle';

const CLIENT_SUPPORTS_RICHTEXT = typeof document.execCommand === 'function'
  && typeof window.getSelection === 'function';

const HIJACK_TEXT_FORMATTING = true;

const simpleTextFormatCommands = new Set(['italic', 'underline', 'bold']);
const isSimpleTextFormattingCommand = name => simpleTextFormatCommands.has(name);
const isCreateAnchorCommand = name => name === 'createLink';

const isRichTextCommand = name => isSimpleTextFormattingCommand(name)
  || isCreateAnchorCommand(name);

export const getCurrentSelection = () => {
  if (!CLIENT_SUPPORTS_RICHTEXT) return console.error('getCurrentSelection: unsupported client');

  try {
    const selection = window.getSelection();
    const range = selection?.getRangeAt?.(0);

    return {
      selection,
      range,
    };
  } catch (error) {
    console.error('getCurrentSelection error:', error);

    return null;
  }
};

export const injectAnchor = (range, url, text) => {
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.textContent = text ?? range?.toString?.();

  range.deleteContents();
  range.insertNode(anchor);

  return anchor;
};


/** Temporary hack until we implement a rich text editor.
  * When the user clicks a text formatting button, we check if they have selected text.
  * If they have, we use document.execCommand to apply the formatting to the selection.
  * Otherwise, we do nothing here and let the button's onClick handler do its thing.
  * eturns true if the command was executed, false otherwise
  */
export const hijackTextFormattingAction = name => {
  if (
    !HIJACK_TEXT_FORMATTING
    || !CLIENT_SUPPORTS_RICHTEXT
    || !isRichTextCommand(name)
  ) return false;

  const selection = window.getSelection();
  const selectedText = selection?.toString?.();
  const hasSelection = typeof selectedText === 'string' && selectedText.length > 0;

  if (hasSelection && isSimpleTextFormattingCommand(name)) {
    return document.execCommand(name);
  }

  return false;
};

export const ToolbarToggle = ({
  className,
  onClick: onButtonClick,
  value,
  setValue,
  inline,
  icon,
  label,
  tooltip,
  name,
}) => {
  const [changed, setChanged] = useState(false);
  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => {
    if (!changed) return;
    if (setValue) setValue(internalValue);
    setChanged(false);
  }, [changed, internalValue, setValue]);

  useLayoutEffect(() => {
    setInternalValue(value);
    setChanged(false);
  }, [value]);

  const onClick = useCallback(e => {
    if (hijackTextFormattingAction(name)) return;
    if (onButtonClick) onButtonClick(e);
    setInternalValue(toggle => !toggle);
    setChanged(true);
  }, [name, onButtonClick]);

  const ref = useRef(null);

  return (
    <div
      className={className}
      ref={ref}
      role="button"
      tabIndex="0"
      onClick={onClick}
      css={[
        toolbarButtonStyle,
        inline && internalValue && toolbarFieldActiveStyle,
        css`
          display: flex;
          flex-direction: row;
        `,
        inline
          && internalValue
          && css`
            background: rgba(255, 255, 255, 0.2);
            :hover {
              background: none;
            }
          `,
      ]}
    >
      <div
        css={[
          css`flex: 0 0 auto;`,
          !inline && css`margin-right: 0.5em;`,
        ]}
      >
        <FieldLabel
          icon={icon}
          label={label}
          tooltip={tooltip}
          parentRef={ref}
        />
      </div>
      {!inline && (
        <div
          css={css`
            margin-left: auto;
            flex: 0 0 auto;
          `}
        >
          <Toggle value={internalValue} />
        </div>
      )}
    </div>
  );
};
